<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Reports & Analytics</h1>
        <div class="gl-breadcrumb">
          <router-link to="/">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span>Reports</span>
        </div>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="gl-filter-bar">
      <!-- Row 1: Quick presets (auto-apply) -->
      <div class="gl-filter-quick-row">
        <span class="gl-filter-quick-label">Quick</span>
        <div class="gl-preset-pills">
          <button
            v-for="p in presets" :key="p.value"
            class="gl-preset-pill"
            :class="{ 'gl-preset-active': activePreset === p.value }"
            @click="applyPreset(p.value)"
          >{{ p.label }}</button>
        </div>
      </div>

      <!-- Row 2: Manual date range + branch + Apply -->
      <div class="gl-filter-manual-row">
        <div class="gl-filter-dates">
          <div class="gl-date-group">
            <label class="gl-filter-label">From</label>
            <input type="date" class="gl-date-input" v-model="filters.start_date" @change="activePreset = null">
          </div>
          <div class="gl-date-sep">→</div>
          <div class="gl-date-group">
            <label class="gl-filter-label">To</label>
            <input type="date" class="gl-date-input" v-model="filters.end_date" @change="activePreset = null">
          </div>
        </div>

        <div class="gl-filter-branch">
          <label class="gl-filter-label">Branch</label>
          <div class="gl-select-wrap">
            <select class="gl-select gl-select-sm" v-model="filters.outlet_id">
              <option value="">All Branches</option>
              <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
            </select>
            <svg class="gl-select-chevron" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>

        <button class="gl-btn-apply" @click="fetchActive">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          Apply
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="gl-tab-bar">
      <button
        v-for="t in tabs" :key="t.key"
        class="gl-tab"
        :class="{ 'gl-tab-active': activeTab === t.key }"
        @click="switchTab(t.key)"
      >
        <component :is="'span'" class="gl-tab-icon" v-html="t.svg"></component>
        {{ t.label }}
      </button>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="gl-skel-layout">
      <div class="gl-skel-stats">
        <div v-for="i in 3" :key="i" class="gl-skel-stat-card">
          <div class="gl-skel gl-skel-line" style="width:50%;margin-bottom:10px"></div>
          <div class="gl-skel gl-skel-line gl-skel-lg" style="width:70%"></div>
        </div>
      </div>
      <div class="gl-skel-chart">
        <div class="gl-skel gl-skel-line" style="width:180px;height:20px;margin-bottom:20px"></div>
        <div style="display:flex;align-items:flex-end;gap:8px;height:200px;padding-top:24px">
          <div v-for="i in 12" :key="i" class="gl-skel" :style="`width:100%;height:${30 + Math.random()*140}px;border-radius:6px`"></div>
        </div>
      </div>
    </div>

    <!-- ── SALES TAB ── -->
    <div v-else-if="activeTab === 'sales' && salesData">
      <!-- Stats strip -->
      <div class="gl-stats-strip gl-stats-3">
        <div class="gl-stat-card">
          <div class="gl-stat-label">Revenue</div>
          <div class="gl-stat-val gl-stat-teal">RM {{ fmt(salesData.summary.revenue) }}</div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-label">Orders</div>
          <div class="gl-stat-val">{{ salesData.summary.orders }}</div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-label">Avg Order Value</div>
          <div class="gl-stat-val">RM {{ fmt(salesData.summary.avg_order_value) }}</div>
        </div>
      </div>

      <!-- Chart -->
      <div class="gl-chart-card">
        <div class="gl-chart-header">
          <h2 class="gl-chart-title">Revenue Trend</h2>
          <div class="gl-chart-meta">{{ filters.start_date }} — {{ filters.end_date }}</div>
        </div>
        <div class="gl-chart-body">
          <apexchart
            v-if="salesSeries.series[0].data.length"
            type="area" height="300"
            :options="salesSeries.options"
            :series="salesSeries.series"
          ></apexchart>
          <div v-else class="gl-no-data">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            <span>No revenue data in this range</span>
          </div>
        </div>
      </div>

      <!-- By Branch table -->
      <div class="gl-table-card">
        <div class="gl-table-header">
          <h2 class="gl-chart-title">Revenue by Branch</h2>
        </div>
        <div class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th>Branch</th>
                <th class="gl-th-right">Orders</th>
                <th class="gl-th-right">Revenue</th>
                <th class="gl-th-bar">Share</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="b in salesData.by_branch" :key="b.outlet_id">
                <td class="gl-td-name">{{ b.outlet_name }}</td>
                <td class="gl-td-right">{{ b.orders }}</td>
                <td class="gl-td-right gl-td-bold">RM {{ fmt(b.revenue) }}</td>
                <td class="gl-td-bar">
                  <div class="gl-bar-track">
                    <div class="gl-bar-fill" :style="`width:${branchShare(b.revenue, salesData.by_branch)}%`"></div>
                  </div>
                </td>
              </tr>
              <tr v-if="!salesData.by_branch.length">
                <td colspan="4" class="gl-td-empty">No data for this period</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ── ORDERS TAB ── -->
    <div v-else-if="activeTab === 'orders' && ordersData">
      <div class="gl-stats-strip gl-stats-4">
        <div class="gl-stat-card">
          <div class="gl-stat-label">Total Orders</div>
          <div class="gl-stat-val">{{ ordersData.summary.total_orders }}</div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-label">Completed</div>
          <div class="gl-stat-val gl-stat-teal">{{ ordersData.summary.completed }}</div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-label">Cancelled</div>
          <div class="gl-stat-val gl-stat-red">{{ ordersData.summary.cancelled }}</div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-label">Pending</div>
          <div class="gl-stat-val gl-stat-amber">{{ ordersData.summary.pending }}</div>
        </div>
      </div>

      <div class="gl-charts-2col">
        <div class="gl-chart-card">
          <div class="gl-chart-header">
            <h2 class="gl-chart-title">By Status</h2>
          </div>
          <div class="gl-chart-body">
            <apexchart v-if="hasKeys(ordersData.by_status)" type="donut" height="260"
              :options="donutOptions(ordersData.by_status, ['#900C27','#3b82f6','#f59e0b','#ef4444','#6b7280'])"
              :series="Object.values(ordersData.by_status)">
            </apexchart>
            <div v-else class="gl-no-data"><span>No data</span></div>
          </div>
        </div>

        <div class="gl-chart-card">
          <div class="gl-chart-header">
            <h2 class="gl-chart-title">By Order Type</h2>
          </div>
          <div class="gl-chart-body">
            <apexchart v-if="hasKeys(ordersData.by_order_type)" type="donut" height="260"
              :options="donutOptions(ordersData.by_order_type, ['#900C27','#3b82f6','#f59e0b'])"
              :series="Object.values(ordersData.by_order_type)">
            </apexchart>
            <div v-else class="gl-no-data"><span>No data</span></div>
          </div>
        </div>
      </div>

      <div class="gl-chart-card">
        <div class="gl-chart-header">
          <h2 class="gl-chart-title">Daily Order Volume</h2>
        </div>
        <div class="gl-chart-body">
          <apexchart v-if="ordersData.daily.length" type="bar" height="280"
            :options="ordersTimelineOptions"
            :series="ordersTimelineSeries">
          </apexchart>
          <div v-else class="gl-no-data"><span>No data</span></div>
        </div>
      </div>
    </div>

    <!-- ── PRODUCTS TAB ── -->
    <div v-else-if="activeTab === 'products' && productsData">
      <div class="gl-table-card">
        <div class="gl-table-header">
          <h2 class="gl-chart-title">Top Products</h2>
          <span class="gl-table-sub">by completed orders in selected period</span>
        </div>
        <div class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th style="width:48px">#</th>
                <th>Product</th>
                <th class="gl-th-right">Qty Sold</th>
                <th class="gl-th-right">Orders</th>
                <th class="gl-th-right">Revenue</th>
                <th class="gl-th-bar">Revenue Bar</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, i) in productsData.top_products" :key="p.product_id" :class="{ 'gl-row-gold': i === 0, 'gl-row-silver': i === 1, 'gl-row-bronze': i === 2 }">
                <td><span class="gl-rank-badge" :class="rankClass(i)">{{ i + 1 }}</span></td>
                <td class="gl-td-name">{{ p.product_name }}</td>
                <td class="gl-td-right">{{ p.qty_sold }}</td>
                <td class="gl-td-right">{{ p.order_count }}</td>
                <td class="gl-td-right gl-td-bold">RM {{ fmt(p.revenue) }}</td>
                <td class="gl-td-bar">
                  <div class="gl-bar-track">
                    <div class="gl-bar-fill" :style="`width:${productShare(p.revenue, productsData.top_products)}%`"></div>
                  </div>
                </td>
              </tr>
              <tr v-if="!productsData.top_products.length">
                <td colspan="6" class="gl-td-empty">No sales in this range</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ── CUSTOMERS TAB ── -->
    <div v-else-if="activeTab === 'customers' && customersData">
      <div class="gl-stats-strip gl-stats-4">
        <div class="gl-stat-card">
          <div class="gl-stat-label">Active Customers</div>
          <div class="gl-stat-val gl-stat-teal">{{ customersData.summary.active_customers }}</div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-label">New Customers</div>
          <div class="gl-stat-val">{{ customersData.summary.new_customers }}</div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-label">Returning</div>
          <div class="gl-stat-val">{{ customersData.summary.returning_customers }}</div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-label">New Signups</div>
          <div class="gl-stat-val">{{ customersData.summary.new_signups_total }}</div>
        </div>
      </div>

      <div class="gl-table-card">
        <div class="gl-table-header">
          <h2 class="gl-chart-title">Top Spenders</h2>
          <span class="gl-table-sub">ranked by total spend in selected period</span>
        </div>
        <div class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th style="width:48px">#</th>
                <th>Customer</th>
                <th>Phone</th>
                <th class="gl-th-right">Orders</th>
                <th class="gl-th-right">Total Spent</th>
                <th class="gl-th-bar">Spend Share</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in customersData.top_spenders" :key="c.user_id" :class="{ 'gl-row-gold': i === 0, 'gl-row-silver': i === 1, 'gl-row-bronze': i === 2 }">
                <td><span class="gl-rank-badge" :class="rankClass(i)">{{ i + 1 }}</span></td>
                <td>
                  <div class="gl-customer-cell">
                    <div class="gl-cust-avatar" :style="`background:${avatarColor(c.name)}`">{{ initials(c.name) }}</div>
                    <span class="gl-td-name">{{ c.name }}</span>
                  </div>
                </td>
                <td class="gl-td-meta">{{ c.phone || '—' }}</td>
                <td class="gl-td-right">{{ c.orders }}</td>
                <td class="gl-td-right gl-td-bold">RM {{ fmt(c.total_spent) }}</td>
                <td class="gl-td-bar">
                  <div class="gl-bar-track">
                    <div class="gl-bar-fill" :style="`width:${customerShare(c.total_spent, customersData.top_spenders)}%`"></div>
                  </div>
                </td>
              </tr>
              <tr v-if="!customersData.top_spenders.length">
                <td colspan="6" class="gl-td-empty">No data for this period</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ownerReportsApi, ownerBranchApi } from '@/services/ownerApi';

const AVATAR_PALETTE = ['#900C27','#2563eb','#d97706','#7c3aed','#be185d','#0891b2'];

export default {
  name: 'OwnerReports',
  data() {
    const fmtDate = d => [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
    ].join('-');
    const today = fmtDate(new Date());
    const start = fmtDate(new Date(Date.now() - 29 * 86400000));
    return {
      loading: false,
      activeTab: 'sales',
      activePreset: '30',
      branches: [],
      filters: { start_date: start, end_date: today, outlet_id: '' },
      salesData: null,
      ordersData: null,
      productsData: null,
      customersData: null,
      tabs: [
        { key: 'sales',     label: 'Sales',     svg: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>' },
        { key: 'orders',    label: 'Orders',    svg: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>' },
        { key: 'products',  label: 'Products',  svg: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>' },
        { key: 'customers', label: 'Customers', svg: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>' },
      ],
      presets: [
        { label: '7d', value: '7' },
        { label: '30d', value: '30' },
        { label: '90d', value: '90' },
        { label: 'MTD', value: 'mtd' },
        { label: 'YTD', value: 'ytd' },
      ]
    };
  },
  computed: {
    salesSeries() {
      const d = this.salesData?.series || [];
      return {
        options: {
          chart: { toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent', animations: { enabled: true, speed: 600 } },
          xaxis: { categories: d.map(r => r.period?.slice(0, 10)), labels: { style: { colors: '#8a9e9a', fontSize: '11px' } }, axisBorder: { show: false }, axisTicks: { show: false } },
          yaxis: { labels: { formatter: v => 'RM ' + this.fmt(v), style: { colors: '#8a9e9a', fontSize: '11px' } } },
          dataLabels: { enabled: false },
          stroke: { curve: 'smooth', width: 2.5 },
          colors: ['#900C27'],
          fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.28, opacityTo: 0.02, stops: [0, 100] } },
          grid: { borderColor: '#eef0ef', strokeDashArray: 4 },
          tooltip: { y: { formatter: v => 'RM ' + this.fmt(v) } },
          theme: { mode: 'light' }
        },
        series: [{ name: 'Revenue', data: d.map(r => Number(r.revenue) || 0) }]
      };
    },
    ordersTimelineOptions() {
      const d = this.ordersData?.daily || [];
      return {
        chart: { toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent', stacked: true },
        xaxis: { categories: d.map(r => r.date), labels: { style: { colors: '#8a9e9a', fontSize: '11px' } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { style: { colors: '#8a9e9a', fontSize: '11px' } } },
        colors: ['#900C27', '#ef4444'],
        dataLabels: { enabled: false },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 4 } },
        grid: { borderColor: '#eef0ef', strokeDashArray: 4 },
        legend: { position: 'top', fontSize: '12px' },
      };
    },
    ordersTimelineSeries() {
      const d = this.ordersData?.daily || [];
      return [
        { name: 'Completed', data: d.map(r => Number(r.completed) || 0) },
        { name: 'Cancelled', data: d.map(r => Number(r.cancelled) || 0) }
      ];
    }
  },
  mounted() {
    this.fetchBranches();
    this.fetchActive();
  },
  methods: {
    async fetchBranches() {
      try {
        const r = await ownerBranchApi.list({ per_page: 100 });
        this.branches = r.data.data;
      } catch {}
    },
    params() {
      const p = { start_date: this.filters.start_date, end_date: this.filters.end_date };
      if (this.filters.outlet_id) p.outlet_id = this.filters.outlet_id;
      if (this.activeTab === 'sales') p.group_by = 'day';
      return p;
    },
    switchTab(key) {
      this.activeTab = key;
      this.fetchActive();
    },
    async fetchActive() {
      try {
        this.loading = true;
        const p = this.params();
        if (this.activeTab === 'sales') {
          const r = await ownerReportsApi.sales(p);
          this.salesData = r.data.data;
        } else if (this.activeTab === 'orders') {
          const r = await ownerReportsApi.orders(p);
          this.ordersData = r.data.data;
        } else if (this.activeTab === 'products') {
          const r = await ownerReportsApi.products(p);
          this.productsData = r.data.data;
        } else if (this.activeTab === 'customers') {
          const r = await ownerReportsApi.customers(p);
          this.customersData = r.data.data;
        }
      } catch (e) {
        console.error('Report load failed', e);
      } finally {
        this.loading = false;
      }
    },
    localDate(d) {
      return [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0'),
      ].join('-');
    },
    applyPreset(v) {
      this.activePreset = v;
      const today = new Date();
      let start;
      if (v === 'mtd') {
        start = new Date(today.getFullYear(), today.getMonth(), 1);
      } else if (v === 'ytd') {
        start = new Date(today.getFullYear(), 0, 1);
      } else {
        start = new Date(Date.now() - (Number(v) - 1) * 86400000);
      }
      this.filters.start_date = this.localDate(start);
      this.filters.end_date = this.localDate(today);
      this.fetchActive();
    },
    fmt(n) {
      const num = Number(n) || 0;
      return num.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    hasKeys(obj) {
      return obj && Object.keys(obj).length > 0;
    },
    donutOptions(obj, colors) {
      return {
        labels: Object.keys(obj).map(k => k.replace(/_/g, ' ')),
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: { enabled: true, formatter: (v) => v.toFixed(0) + '%' },
        colors: colors || ['#900C27', '#3b82f6', '#f59e0b', '#ef4444', '#6b7280'],
        chart: { fontFamily: 'inherit' },
        plotOptions: { pie: { donut: { size: '60%' } } },
      };
    },
    branchShare(rev, list) {
      const max = Math.max(...list.map(b => Number(b.revenue) || 0));
      return max ? Math.round((Number(rev) / max) * 100) : 0;
    },
    productShare(rev, list) {
      const max = Math.max(...list.map(p => Number(p.revenue) || 0));
      return max ? Math.round((Number(rev) / max) * 100) : 0;
    },
    customerShare(spent, list) {
      const max = Math.max(...list.map(c => Number(c.total_spent) || 0));
      return max ? Math.round((Number(spent) / max) * 100) : 0;
    },
    rankClass(i) {
      if (i === 0) return 'gl-rank-gold';
      if (i === 1) return 'gl-rank-silver';
      if (i === 2) return 'gl-rank-bronze';
      return 'gl-rank-default';
    },
    initials(name) {
      return (name || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
    },
    avatarColor(name) {
      let hash = 0;
      for (let i = 0; i < (name || '').length; i++) hash = (name.charCodeAt(i) + hash * 31) | 0;
      return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
    }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-page { padding: 28px 32px; max-width: 1280px; }
.gl-page-header { margin-bottom: 24px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #1a2e2b; margin: 0 0 4px; letter-spacing: -0.3px; }
.gl-breadcrumb { font-size: 12px; color: #8a9e9a; display: flex; align-items: center; gap: 6px; }
.gl-breadcrumb a { color: #8a9e9a; text-decoration: none; }
.gl-breadcrumb a:hover { color: #900C27; }
.gl-breadcrumb-sep { opacity: .5; }

/* ── Filter Bar ── */
.gl-filter-bar {
  display: flex; flex-direction: column; gap: 12px;
  margin-bottom: 24px;
}

/* Row 1: quick presets */
.gl-filter-quick-row { display: flex; align-items: center; gap: 10px; }
.gl-filter-quick-label {
  font-size: 11px; font-weight: 700; color: #a0b5b0; text-transform: uppercase;
  letter-spacing: .5px; white-space: nowrap; flex-shrink: 0;
}

/* Row 2: manual inputs + apply */
.gl-filter-manual-row {
  display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;
  padding-top: 12px; border-top: 1px solid #f0f3f2;
}

.gl-filter-dates { display: flex; align-items: flex-end; gap: 8px; }
.gl-date-group { display: flex; flex-direction: column; }
.gl-date-sep { font-size: 14px; color: #aab8b5; margin-bottom: 10px; padding: 0 2px; }
.gl-filter-branch { display: flex; flex-direction: column; }
.gl-filter-label { font-size: 11px; font-weight: 600; color: #6b8a85; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 6px; }

.gl-date-input {
  padding: 8px 11px; font-size: 13px; color: #1a2e2b;
  background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px;
  outline: none; font-family: inherit;
}
.gl-date-input:focus { border-color: #900C27; background: #fff; }

.gl-select-wrap { position: relative; }
.gl-select {
  appearance: none; padding: 8px 30px 8px 11px; font-size: 13px; color: #1a2e2b;
  background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px;
  outline: none; cursor: pointer; font-family: inherit;
}
.gl-select-sm { min-width: 150px; }
.gl-select:focus { border-color: #900C27; background: #fff; }
.gl-select-chevron { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #8a9e9a; }

/* ── Preset pills ── */
.gl-preset-pills { display: flex; gap: 5px; align-items: center; }
.gl-preset-pill {
  padding: 7px 12px; font-size: 12.5px; font-weight: 500; color: #5a7470;
  background: #f0f5f4; border: 1.5px solid #dde8e6; border-radius: 20px;
  cursor: pointer; transition: all .15s;
}
.gl-preset-pill:hover { border-color: #900C27; color: #900C27; background: #f0f8f5; }
.gl-preset-active { background: #900C27 !important; color: #fff !important; border-color: #900C27 !important; }

.gl-btn-apply {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px; font-size: 13px; font-weight: 600;
  background: #900C27; color: #fff; border: none; border-radius: 8px;
  cursor: pointer; transition: background .15s;
}
.gl-btn-apply:hover { background: #1a5248; }

/* ── Tabs ── */
.gl-tab-bar { display: flex; gap: 2px; border-bottom: 2px solid #eef0ef; margin-bottom: 24px; }
.gl-tab { display: flex; align-items: center; gap: 7px; padding: 10px 18px; font-size: 13.5px; font-weight: 500; color: #6b7f7b; background: none; border: none; border-bottom: 2px solid transparent; margin-bottom: -2px; cursor: pointer; transition: color .15s, border-color .15s; }
.gl-tab:hover { color: #900C27; }
.gl-tab-active { color: #900C27; border-bottom-color: #900C27; font-weight: 600; }
.gl-tab-icon { display: flex; align-items: center; }

/* ── Stats strip ── */
.gl-stats-strip { display: grid; gap: 16px; margin-bottom: 20px; }
.gl-stats-3 { grid-template-columns: repeat(3, 1fr); }
.gl-stats-4 { grid-template-columns: repeat(4, 1fr); }

.gl-stat-card {
  background: #fff; border: 1px solid #eaedec; border-radius: 12px;
  padding: 18px 20px;
}
.gl-stat-label { font-size: 12px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 8px; }
.gl-stat-val { font-size: 26px; font-weight: 700; color: #1a2e2b; letter-spacing: -0.5px; }
.gl-stat-teal { color: #900C27; }
.gl-stat-red { color: #dc2626; }
.gl-stat-amber { color: #d97706; }

/* ── Chart cards ── */
.gl-chart-card {
  background: #fff; border: 1px solid #eaedec; border-radius: 12px;
  margin-bottom: 20px; overflow: hidden;
}
.gl-chart-header { display: flex; align-items: baseline; gap: 10px; padding: 18px 20px 0; }
.gl-chart-title { font-size: 14.5px; font-weight: 700; color: #1a2e2b; margin: 0; }
.gl-chart-meta { font-size: 11.5px; color: #a0b5b0; }
.gl-chart-body { padding: 12px 8px 8px; }

.gl-charts-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }

/* ── Table cards ── */
.gl-table-card {
  background: #fff; border: 1px solid #eaedec; border-radius: 12px;
  margin-bottom: 20px; overflow: hidden;
}
.gl-table-header { display: flex; align-items: baseline; gap: 10px; padding: 18px 20px 14px; border-bottom: 1px solid #f0f3f2; }
.gl-table-sub { font-size: 12px; color: #a0b5b0; }
.gl-table-wrap { overflow-x: auto; }

.gl-table { width: 100%; border-collapse: collapse; }
.gl-table thead th {
  font-size: 11.5px; font-weight: 700; color: #6b8a85; text-transform: uppercase;
  letter-spacing: .3px; padding: 11px 16px; background: #f8faf9;
  border-bottom: 1px solid #eef0ef; white-space: nowrap;
}
.gl-table tbody tr { border-bottom: 1px solid #f5f7f6; transition: background .12s; }
.gl-table tbody tr:last-child { border-bottom: none; }
.gl-table tbody tr:hover { background: #f8faf9; }
.gl-table tbody td { padding: 12px 16px; font-size: 13.5px; color: #2a3d3a; }

.gl-th-right { text-align: right; }
.gl-th-bar { text-align: left; width: 140px; }
.gl-td-right { text-align: right; font-variant-numeric: tabular-nums; }
.gl-td-bold { font-weight: 600; color: #1a2e2b; }
.gl-td-name { font-weight: 500; color: #1a2e2b; }
.gl-td-meta { color: #8a9e9a; font-size: 13px; }
.gl-td-bar { width: 140px; }
.gl-td-empty { text-align: center; color: #a0b5b0; padding: 32px !important; font-style: italic; }

/* ── Bar visualisation ── */
.gl-bar-track { height: 6px; background: #eef0ef; border-radius: 6px; overflow: hidden; }
.gl-bar-fill { height: 100%; background: linear-gradient(90deg, #900C27, #3d9e8c); border-radius: 6px; transition: width .4s ease; }

/* ── Rank badges ── */
.gl-rank-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; border-radius: 8px;
  font-size: 12px; font-weight: 700;
}
.gl-rank-gold   { background: #fef3c7; color: #92400e; }
.gl-rank-silver { background: #f3f4f6; color: #374151; }
.gl-rank-bronze { background: #fdf0e6; color: #92400e; }
.gl-rank-default { background: #f3f6f5; color: #5a7470; }

.gl-row-gold   td:first-child { border-left: 3px solid #f59e0b; }
.gl-row-silver td:first-child { border-left: 3px solid #9ca3af; }
.gl-row-bronze td:first-child { border-left: 3px solid #cd7c3a; }

/* ── Customer cell ── */
.gl-customer-cell { display: flex; align-items: center; gap: 9px; }
.gl-cust-avatar {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: #fff;
}

/* ── No data ── */
.gl-no-data { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #a0b5b0; font-size: 13px; padding: 48px 24px; }

/* ── Skeleton ── */
.gl-skel-layout { display: flex; flex-direction: column; gap: 20px; }
.gl-skel-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.gl-skel-stat-card { background: #fff; border: 1px solid #eaedec; border-radius: 12px; padding: 18px 20px; }
.gl-skel-chart { background: #fff; border: 1px solid #eaedec; border-radius: 12px; padding: 20px; }
.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-skel-line { height: 14px; }
.gl-skel-lg { height: 28px !important; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Mobile ── */
@media (max-width: 767px) {
  /* Page */
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-title { font-size: 18px; }
  .gl-breadcrumb { display: none; }
  .gl-page-header { margin-bottom: 14px; }

  /* Filter bar: transparent, stacked */
  .gl-filter-bar {
    background: transparent;
    border: none;
    padding: 0;
    gap: 10px;
    margin-bottom: 16px;
  }

  /* Quick presets row: scrollable */
  .gl-filter-quick-row { gap: 8px; }
  .gl-preset-pills {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    flex-wrap: nowrap;
    padding-bottom: 2px;
  }
  .gl-preset-pills::-webkit-scrollbar { display: none; }
  .gl-preset-pill { flex-shrink: 0; padding: 6px 11px; font-size: 12px; }

  /* Manual row: stack vertically */
  .gl-filter-manual-row {
    flex-direction: column;
    gap: 10px;
    padding-top: 10px;
  }

  /* Date inputs: side-by-side, fill row */
  .gl-filter-dates { gap: 6px; width: 100%; }
  .gl-date-group { flex: 1; }
  .gl-date-input { width: 100%; box-sizing: border-box; font-size: 12.5px; }

  /* Branch select: full width */
  .gl-filter-branch { width: 100%; }
  .gl-select-sm { min-width: 0; width: 100%; box-sizing: border-box; }

  /* Apply button: full width */
  .gl-btn-apply { width: 100%; justify-content: center; }

  /* Tabs: horizontal scroll */
  .gl-tab-bar {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    flex-wrap: nowrap;
    margin-bottom: 16px;
  }
  .gl-tab-bar::-webkit-scrollbar { display: none; }
  .gl-tab { flex-shrink: 0; padding: 9px 14px; font-size: 13px; white-space: nowrap; }

  /* Stats strips: 2-column grid */
  .gl-stats-3 { grid-template-columns: 1fr 1fr; }
  .gl-stats-4 { grid-template-columns: 1fr 1fr; }
  .gl-stat-card { padding: 14px 14px; }
  .gl-stat-label { font-size: 11px; }
  .gl-stat-val { font-size: 20px; }

  /* Charts 2-col: single column */
  .gl-charts-2col { grid-template-columns: 1fr; gap: 0; }

  /* Chart/table card padding */
  .gl-chart-header { padding: 14px 14px 0; flex-wrap: wrap; gap: 4px; }
  .gl-chart-body { padding: 8px 4px 4px; }
  .gl-table-header { padding: 14px 14px 12px; flex-wrap: wrap; }

  /* Tables: hide bar/share columns, enable scroll */
  .gl-table { min-width: 360px; }
  .gl-table thead th.gl-th-bar,
  .gl-table tbody td.gl-td-bar { display: none; }

  /* Skeleton: 2-col */
  .gl-skel-stats { grid-template-columns: 1fr 1fr; }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb, body.dark-only .gl-breadcrumb a { color: #5a7470; }

body.dark-only .gl-filter-manual-row { border-top-color: #2a3d3a; }
body.dark-only .gl-filter-quick-label { color: #3d5450; }
body.dark-only .gl-date-input { background: #16211f; border-color: #2a3d3a; color: #e0eded; }
body.dark-only .gl-date-input:focus { border-color: #4caf9a; }
body.dark-only .gl-select { background: #16211f; border-color: #2a3d3a; color: #e0eded; }
body.dark-only .gl-select:focus { border-color: #4caf9a; }
body.dark-only .gl-preset-pill { background: #16211f; border-color: #2a3d3a; color: #8ab5ad; }
body.dark-only .gl-preset-pill:hover { border-color: #4caf9a; color: #4caf9a; background: #1c2f2b; }
body.dark-only .gl-preset-active { background: #900C27 !important; color: #fff !important; border-color: #900C27 !important; }

body.dark-only .gl-tab-bar { border-bottom-color: #2a3432; }
body.dark-only .gl-tab { color: #5a7470; }
body.dark-only .gl-tab:hover { color: #4caf9a; }
body.dark-only .gl-tab-active { color: #4caf9a; border-bottom-color: #4caf9a; }

body.dark-only .gl-stat-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-stat-val { color: #e0eded; }
body.dark-only .gl-stat-teal { color: #4caf9a; }

body.dark-only .gl-chart-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-chart-title { color: #e0eded; }

body.dark-only .gl-table-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-table-header { border-bottom-color: #2a3d3a; }
body.dark-only .gl-chart-title { color: #e0eded; }
body.dark-only .gl-table thead th { background: #16211f; color: #5a7470; border-bottom-color: #2a3d3a; }
body.dark-only .gl-table tbody tr { border-bottom-color: #222f2d; }
body.dark-only .gl-table tbody tr:hover { background: #1c2f2b; }
body.dark-only .gl-table tbody td { color: #c0d4d0; }
body.dark-only .gl-td-name { color: #e0eded; }
body.dark-only .gl-td-bold { color: #e0eded; }
body.dark-only .gl-bar-track { background: #2a3d3a; }

body.dark-only .gl-skel-stat-card, body.dark-only .gl-skel-chart { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-no-data { color: #5a7470; }

@media (max-width: 767px) {
  body.dark-only .gl-filter-bar { background: transparent; border: none; }
  body.dark-only .gl-filter-manual-row { border-top: none; }
}
</style>
